import * as moment from 'moment';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

/**
 * Date comparator for AG Grid filters
 */
export function dateComparator(filterLocalDateAtMidnight: Date, cellValue: string): number {
  if (!cellValue) return -1;
  if (cellValue === '~') return -1;
  const dateParts = cellValue.split('-');
  if (dateParts.length !== 3) return -1;
  const year = parseInt(dateParts[0], 10);
  const month = parseInt(dateParts[1], 10) - 1;
  const day = parseInt(dateParts[2], 10);
  const cellDate = new Date(year, month, day);
  const cellDateOnly = new Date(cellDate.setHours(0, 0, 0, 0));
  const filterDateOnly = new Date(filterLocalDateAtMidnight.setHours(0, 0, 0, 0));
  if (cellDateOnly < filterDateOnly) return -1;
  if (cellDateOnly > filterDateOnly) return 1;
  return 0;
}

/**
 * Date filter params for AG Grid (standard)
 */
export const dateFilterParams = {
  comparator: dateComparator,
  inRangeFloatingFilterDateFormat: 'YY-MM-DD',
  maxNumConditions: 1,
  debounceMs: 1000,
  closeOnApply: true,
  inRangeInclusive: true,
  filterOptions: ['equals', 'inRange', 'before', 'after'],
};

/**
 * Date filter params for trade/investments (uses lessThan/greaterThan)
 */
export const dateFilterParamsForTradeInvestments = {
  comparator: dateComparator,
  inRangeFloatingFilterDateFormat: 'YY-MM-DD',
  maxNumConditions: 1,
  debounceMs: 1000,
  closeOnApply: true,
  inRangeInclusive: true,
  filterOptions: ['equals', 'inRange', 'lessThan', 'greaterThan'],
};

/**
 * Date filter params with blank option (localized)
 */
export const dateFilterParamsWithEmptyLocal = {
  comparator: dateComparator,
  inRangeFloatingFilterDateFormat: 'YY-MM-DD',
  maxNumConditions: 1,
  debounceMs: 1000,
  closeOnApply: true,
  inRangeInclusive: true,
  filterOptions: ['equals', 'blank', 'inRange', 'lessThan', 'greaterThan'],
};

/**
 * Date filter params with blank and filled options
 */
export const dateFilterParamsWithEmpty = {
  comparator: dateComparator,
  inRangeFloatingFilterDateFormat: 'YY-MM-DD',
  maxNumConditions: 1,
  debounceMs: 1000,
  closeOnApply: true,
  inRangeInclusive: true,
  filterOptions: [
    'equals',
    'blank',
    {
      displayKey: 'Filled',
      displayName: 'Filled',
      predicate: () => true,
      numberOfInputs: 0,
    },
    'inRange',
    'before',
    'after',
  ],
};

/**
 * Parse date string to year/month/day object
 */
export function parseDateString(param: string): { year: number; day: number; month: number } {
  return {
    year: parseInt(moment(param).format('YYYY'), 10),
    day: parseInt(moment(param).format('DD'), 10),
    month: parseInt(moment(param).format('M'), 10),
  };
}

/**
 * Parse date object (NgbDateStruct or similar) to formatted date string
 */
export function parseDate(dateString: any): string {
  if (dateString) {
    const dateMoment = new Date(dateString);
    return !isNaN(dateMoment.getTime()) ? moment(dateString).format('YYYY-MM-DD') : '';
  }
  return dateString;
}

/**
 * Date value formatter for AG Grid
 */
export function dateValueFormatter(params: any) {
  const parsedDate = new Date(params.value);
  if (!isNaN(parsedDate.getTime())) return params.value ? moment(params.value).format('YYYY-MM-DD') : '';
  return params.value;
}

/**
 * Date filter params settings for query building
 */
export function dateFilterParamsSettings(dateFilter: Record<string, string>, key: string): any {
  const queryParams: Record<string, string> = {};
  if (dateFilter['type'] === 'inRange') {
    queryParams[`${[key]}[from]`] = moment(dateFilter['dateFrom']).format('YYYY-MM-DD');
    queryParams[`${[key]}[to]`] = moment(dateFilter['dateTo']).format('YYYY-MM-DD');
    queryParams[`${[key]}[comparator]`] = 'Between';
  } else if (dateFilter['type'] === 'after') {
    queryParams[`${[key]}[from]`] = moment(dateFilter['dateFrom']).format('YYYY-MM-DD');
    queryParams[`${[key]}[comparator]`] = dateFilter['type'].charAt(0).toUpperCase() + dateFilter['type'].slice(1);
  } else if (dateFilter['type'] === 'blank') {
    queryParams[`${[key]}[from]`] = '';
    queryParams[`${[key]}[comparator]`] = dateFilter['type'].charAt(0).toUpperCase() + dateFilter['type'].slice(1);
  } else {
    queryParams[`${[key]}[from]`] = moment(dateFilter?.['dateFrom']).format('YYYY-MM-DD');
    queryParams[`${[key]}[comparator]`] = dateFilter['type'].charAt(0).toUpperCase() + dateFilter['type'].slice(1);
  }

  return queryParams;
}

/**
 * Generate date range from 2010 to current date (monthly)
 */
export function generateDateRange(): { date: string; id: string }[] {
  const currentDate = new Date();
  const startDate = new Date(2010, 0, 1); // January is 0 in JavaScript
  const dateList = [];

  while (startDate <= currentDate) {
    const year = startDate.getFullYear();
    const month = ('0' + (startDate.getMonth() + 1)).slice(-2); // Month is zero-indexed
    const dateString = year + '-' + month;
    dateList.unshift({ date: dateString, id: dateString });

    startDate.setMonth(startDate.getMonth() + 1); // Move to the next month
  }

  return dateList;
}

/**
 * Generate year range from start year to current year
 */
export function generateYearRange(startYear = 2010): { id: string; name: string }[] {
  let yearsList = [];
  const currentYear = new Date().getFullYear();
  while (startYear <= currentYear) {
    yearsList.push({ id: `${startYear}`, name: `${startYear}` });
    startYear++;
  }
  yearsList = yearsList.reverse();
  return [{ id: '', name: 'All' }, ...yearsList];
}

